import { Key, Mail, Save, Shield, User } from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/api";

const Profile = () => {
  const { user } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      toast.success("Mot de passe modifié avec succès");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowPasswordForm(false);
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Erreur lors du changement de mot de passe");
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "superadmin":
        return "👑";
      case "admin":
        return "🛡️";
      case "parent":
        return "👤";
      default:
        return "👤";
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "superadmin":
        return "text-purple-600 bg-purple-100";
      case "admin":
        return "text-blue-600 bg-blue-100";
      case "parent":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Mon profil</h1>
        <p className="text-gray-600">
          Gérez vos informations personnelles et votre mot de passe
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations personnelles */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations personnelles
          </h2>

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{user?.nom}</h3>
                <p className="text-sm text-gray-500">Nom complet</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-gray-600" />
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{user?.email}</h3>
                <p className="text-sm text-gray-500">Adresse email</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-warning-100 rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-warning-600" />
                </div>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{getRoleIcon(user?.role)}</span>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRoleColor(
                      user?.role
                    )}`}
                  >
                    {user?.role === "superadmin"
                      ? "Super Administrateur"
                      : user?.role === "admin"
                      ? "Administrateur"
                      : "Parent"}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Rôle dans le système</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-success-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">✓</span>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  {user?.actif ? "Compte actif" : "Compte inactif"}
                </h3>
                <p className="text-sm text-gray-500">Statut du compte</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sécurité */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sécurité</h2>

          {!showPasswordForm ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Key className="h-6 w-6 text-gray-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Mot de passe</h3>
                  <p className="text-sm text-gray-500">
                    Dernière modification: Non disponible
                  </p>
                </div>
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="btn-secondary"
                >
                  Modifier
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="label">Mot de passe actuel</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="label">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                  className="input-field"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="label">
                  Confirmer le nouveau mot de passe
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="input-field"
                  required
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{loading ? "Modification..." : "Sauvegarder"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Informations système */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Informations système
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              À propos de e-Tampon Parent Connect
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Système de fidélisation et de suivi des parents d'élèves basé sur
              un système de tampons numériques validés par QR code.
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Version:</span>
                <span className="font-medium">1.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Date de création du compte:
                </span>
                <span className="font-medium">
                  {user?.date_creation
                    ? new Date(user.date_creation).toLocaleDateString()
                    : "Non disponible"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Support</h3>
            <p className="text-sm text-gray-600 mb-4">
              Pour toute question ou problème, contactez l'administration de
              l'école.
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">ID utilisateur:</span>
                <span className="font-medium font-mono text-xs">
                  {user?.id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Dernière connexion:</span>
                <span className="font-medium">Maintenant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
